import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { useActiveColumns, alignClass } from "@/components/ActiveColumns";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, FileText, MoreHorizontal, Eye, Pencil, Copy, Ban, Trash2, Printer, MessageCircle, Mail, BellRing, RefreshCw } from "lucide-react";
import { shareBillWhatsApp, shareBillEmail } from "@/lib/sharebill";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";
import { toast } from "sonner";

const SHARE = { phoneKey: "customer_phone", emailKey: "customer_email", nameKey: "customer_name", noKey: "invoice_no", totalKey: "grand_total", label: "Invoice", publicInvoice: true };

const TABS = [
  ["all", "All"], ["draft", "Draft"], ["paid", "Paid"],
  ["partial", "Partial"], ["unpaid", "Unpaid"], ["overdue", "Overdue"], ["cancelled", "Cancelled"],
];

export default function Invoices() {
  const requestWhatsAppSend = useWhatsAppSender();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [reminding, setReminding] = useState(false);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();

  const cols = [
    { key: "invoice_no", label: "Invoice No", cell: (inv) => <span className="font-mono font-medium cursor-pointer hover:text-primary" onClick={() => navigate(`/sales/invoices/${inv.id}`)}>{inv.invoice_no}</span> },
    { key: "invoice_date", label: "Date", cell: (inv) => <span className="text-muted-foreground">{inv.invoice_date}</span> },
    { key: "customer_name", label: "Customer", cell: (inv) => inv.customer_name },
    { key: "grand_total", label: "Amount", align: "right", cell: (inv) => <span className="font-mono">{inr(inv.grand_total)}</span> },
    { key: "balance_due", label: "Balance", align: "right", cell: (inv) => <span className="font-mono">{inv.balance_due > 0 ? inr(inv.balance_due) : "—"}</span> },
    { key: "status", label: "Status", align: "center", cell: (inv) => <StatusBadge status={inv.status} overdue={inv.overdue} /> },
  ];
  const { shown, Picker } = useActiveColumns("invoices", cols);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const r = await http.get("/invoices", { params: { status: tab, search } });
      if (sequence !== requestSequence.current) return;
      setRows(r.data);
      setSelected((current) => current.filter((id) => r.data.some((invoice) => invoice.id === id)));
      setLoadError("");
    } catch (e) {
      if (sequence === requestSequence.current) setLoadError(requestErrorMessage(e, "load invoices"));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [tab, search]);
  useEffect(() => {
    setSelected([]);
    const initial = setTimeout(load, 250);
    const refreshVisible = () => { if (document.visibilityState !== "hidden") load(); };
    const interval = setInterval(refreshVisible, 30000);
    window.addEventListener("focus", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      ++requestSequence.current;
      clearTimeout(initial);
      clearInterval(interval);
      window.removeEventListener("focus", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [load]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id));

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "complete this action")); }
  };
  const cancel = (id) => act(() => http.post(`/invoices/${id}/cancel`), "Invoice cancelled");
  const del = (id) => { if (window.confirm("Delete invoice?")) act(() => http.delete(`/invoices/${id}`), "Invoice deleted"); };
  const duplicate = async (id) => {
    try {
      const { data } = await http.get(`/invoices/${id}`);
      const payload = {
        customer_id: data.customer_id, place_of_supply: data.place_of_supply,
        place_of_supply_code: data.place_of_supply_code, items: data.items,
        shipping: data.shipping, other_charges: data.other_charges, amount_received: 0,
      };
      const res = await http.post("/invoices", payload);
      toast.success("Invoice duplicated"); navigate(`/sales/invoices/${res.data.id}`);
    } catch (e) { toast.error(requestErrorMessage(e, "duplicate invoice")); }
  };

  const bulkCancel = async () => {
    try { for (const id of selected) await http.post(`/invoices/${id}/cancel`); toast.success(`${selected.length} cancelled`); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "cancel selected invoices")); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} invoices?`)) return;
    try { for (const id of selected) await http.delete(`/invoices/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete selected invoices")); }
  };
  const bulkRemind = async () => {
    setReminding(true);
    try {
      const ids = selected.slice(0, 50);
      const { data } = await http.post("/reminders/send", { invoice_ids: ids });
      const bits = [`${data.skipped} skipped (paid/cancelled/no email)`, `${data.failed} failed`];
      if (data.failed_nos?.length) bits.push(`Failed: ${data.failed_nos.join(", ")}`);
      if (selected.length > 50) bits.push("Only first 50 selected were processed");
      toast.success(`${data.sent} reminder(s) sent`, { description: bits.join(" · ") });
      setSelected([]);
    } catch (e) { toast.error(requestErrorMessage(e, "send reminders")); }
    finally { setReminding(false); }
  };

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Sales Invoices</h1>
          <p className="text-sm text-muted-foreground">{loading && rows.length === 0 ? "Loading invoices…" : `${rows.length} invoices`}</p>
        </div>
        <Button onClick={() => navigate("/sales/invoices/new")} data-testid="create-invoice-button"><Plus className="h-4 w-4 mr-1" /> Create Invoice</Button>
      </div>

      <div className="color-tabs flex items-center gap-2 flex-wrap" role="group" aria-label="Filter invoices by status">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`tab-${k}`}
            aria-pressed={tab === k}
            data-tone={`invoice-${k}`}
            className="color-tab px-3 py-1.5 rounded-full text-sm font-medium">
            {label}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        {loadError && <div className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="no-print p-4 border-b flex items-center gap-3 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search invoice no, customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search" />
          </div>
          {selected.length > 0 && (
            <div className="flex items-center gap-2" data-testid="bulk-actions">
              <span className="text-sm text-muted-foreground">{selected.length} selected</span>
              <Button size="sm" variant="outline" onClick={bulkRemind} disabled={reminding} data-testid="bulk-remind-button"><BellRing className="h-4 w-4 mr-1" /> {reminding ? "Sending…" : "Remind"}</Button>
              <Button size="sm" variant="outline" onClick={bulkCancel}><Ban className="h-4 w-4 mr-1" /> Cancel</Button>
              <Button size="sm" variant="outline" className="text-rose-600" onClick={bulkDelete}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-invoices-button"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
            <Button size="sm" variant="print" onClick={() => window.print()} data-testid="print-list-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            {Picker}
          </div>
        </div>
        <div className="overflow-x-auto" id="print-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 w-10 no-print"><Checkbox checked={rows.length > 0 && selected.length === rows.length} onCheckedChange={toggleAll} data-testid="select-all" /></th>
                {shown.map((c) => <th key={c.key} className={`px-4 py-3 font-medium ${alignClass(c.align)}`}>{c.label}</th>)}
                <th className="px-4 py-3 font-medium text-right no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} className="border-t hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 no-print"><Checkbox checked={selected.includes(inv.id)} onCheckedChange={() => toggle(inv.id)} data-testid={`select-${inv.id}`} /></td>
                  {shown.map((c) => <td key={c.key} className={`px-4 py-3 ${alignClass(c.align)}`}>{c.cell(inv)}</td>)}
                  <td className="px-4 py-3 text-right no-print">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 hover:bg-slate-100 rounded" data-testid={`actions-${inv.id}`}><MoreHorizontal className="h-4 w-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/sales/invoices/${inv.id}`)}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/sales/invoices/${inv.id}?print=1`)}><Printer className="h-4 w-4 mr-2" /> Print / PDF</DropdownMenuItem>
                        {!inv.import_summary_only && <>
                          <DropdownMenuItem onClick={() => navigate(`/sales/invoices/${inv.id}/edit`)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => duplicate(inv.id)}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
                        </>}
                        <DropdownMenuSeparator />
                        {!inv.import_summary_only && <>
                          <DropdownMenuItem onClick={() => shareBillWhatsApp(`/invoices/${inv.id}`, SHARE, requestWhatsAppSend)} data-testid={`wa-${inv.id}`}><MessageCircle className="h-4 w-4 mr-2 text-emerald-600" /> Send WhatsApp</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => shareBillEmail(`/invoices/${inv.id}`, SHARE)} data-testid={`email-${inv.id}`}><Mail className="h-4 w-4 mr-2 text-brand-600" /> Send Email</DropdownMenuItem>
                        </>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => cancel(inv.id)}><Ban className="h-4 w-4 mr-2" /> Cancel</DropdownMenuItem>
                        <DropdownMenuItem className="text-rose-600" onClick={() => del(inv.id)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={shown.length + 2} className="px-4 py-12 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />{loading ? "Loading invoices…" : loadError ? "Invoices could not be loaded. Use Retry above." : "No invoices"}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
